import React, { ComponentProps, forwardRef } from "react";

import classNames from "@/helpers/classNames";
import { LinkWithSlug } from "@/router";
import { Menu } from "@headlessui/react";

// Polymorphic typing for button or anchor
type ButtonProps = {
  as?: "button";
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
} & Omit<ComponentProps<"button">, "onClick">;

type AnchorProps = {
  as: "a";
} & Omit<ComponentProps<typeof LinkWithSlug>, "children">;

type SharedProps = {
  text: string;
  disabled?: boolean;
  size?: "sm" | "lg";
  danger?: boolean;
  tooltipText?: string;
  icon?: React.ReactNode;
};

type Props = SharedProps & (ButtonProps | AnchorProps);

const MenuItem = forwardRef<HTMLButtonElement | HTMLAnchorElement, Props>(
  (props, ref) => {
    const {
      text,
      size,
      disabled,
      danger,
      tooltipText,
      icon,
      as = "button",
      ...rest
    } = props;

    const renderContent = () => (
      <>
        {icon && (
          <span className="mr-2 flex shrink-0 items-center justify-center">
            {icon}
          </span>
        )}
        <span className="grow">{text}</span>
        {tooltipText && (
          <span className="shrink-0 text-2xs text-muted-foreground">
            {tooltipText}
          </span>
        )}
      </>
    );

    return (
      <Menu.Item ref={ref as any} disabled={disabled}>
        {({ active }) => {
          const className = classNames(
            "flex w-full text-left text-foreground px-4 py-2 text-sm cursor-pointer",
            {
              "cursor-not-allowed opacity-50": disabled,
              "bg-gray-100 dark:bg-gray-700": active,
              "px-3 py-2 text-xs": size === "sm",
              "text-red-600 dark:text-red-400": danger,
            },
            props.className,
          );

          if (as === "a") {
            const { to, onClick, ...anchorRest } = rest as AnchorProps;
            return (
              <LinkWithSlug
                to={disabled ? undefined : to}
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  if (disabled) {
                    e.preventDefault();
                    return;
                  }
                  e.stopPropagation();
                  onClick?.(e);
                }}
                {...anchorRest}
                aria-disabled={disabled}
                tabIndex={disabled ? -1 : 0}
                className={className}
              >
                {renderContent()}
              </LinkWithSlug>
            );
          }

          // Default: button
          const { onClick, ...buttonRest } = rest as ButtonProps;
          return (
            <button
              ref={ref as React.Ref<HTMLButtonElement>}
              type="button"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                if (disabled) return;
                e.stopPropagation();
                onClick(e);
              }}
              disabled={disabled}
              {...buttonRest}
              className={className}
            >
              {renderContent()}
            </button>
          );
        }}
      </Menu.Item>
    );
  },
);

export default MenuItem;
